import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RootState, useAppSelector } from '../../../../../../store';

export interface TwoLineSelectItem<T = string> {
    value: T;
    label: string;
    subLabel?: string;
}

interface TwoLineSelectProps<T = string> {
    items: TwoLineSelectItem<T>[];
    value: T | undefined | null;
    onChange: (value: T) => void;
    placeholder?: string;
    classList?: string;
    disabled?: boolean;
    required?: boolean;
    id?: string;
    // Optional custom sort comparator. If provided, items will be sorted using this comparator.
    sortComparator?: (a: TwoLineSelectItem<T>, b: TwoLineSelectItem<T>) => number;
}

const TwoLineSelect = function <T>({
    items,
    value,
    onChange,
    placeholder = undefined,
    classList,
    disabled = false,
    id,
    sortComparator,
}: TwoLineSelectProps<T>) {
    const [open, setOpen] = useState(false);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');

    const selectedItem = useMemo(() => items.find((i) => i.value === value), [items, value]);

    useEffect(() => {
        function onDocClick(e: MouseEvent) {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        if (open) document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 0);
            const idx = items.findIndex((i) => i.value === value);
            setActiveIndex(idx >= 0 ? idx : 0);
            setQuery('');
        }
    }, [open, items, value]);

    useEffect(() => {
        if (open) {
            setOpen(false);
            setQuery('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const filteredAndSortedItems = useMemo(() => {
        const q = query.trim().toLowerCase();
        let arr = q
            ? items.filter((i) =>
                  [i.label, i.subLabel].filter(Boolean).some((s) => String(s).toLowerCase().includes(q)),
              )
            : items.slice();

        if (sortComparator) {
            arr.sort(sortComparator);
        }
        return arr;
    }, [items, query, sortComparator]);

    useEffect(() => {
        setActiveIndex((prev) => {
            if (filteredAndSortedItems.length === 0) return -1;
            if (prev < 0) return 0;
            if (prev >= filteredAndSortedItems.length) return filteredAndSortedItems.length - 1;
            return prev;
        });
    }, [filteredAndSortedItems]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (!open) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((idx) => Math.min((idx < 0 ? -1 : idx) + 1, filteredAndSortedItems.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((idx) => Math.max((idx < 0 ? 0 : idx) - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const item = activeIndex >= 0 ? filteredAndSortedItems[activeIndex] : undefined;
            if (item) {
                onChange(item.value);
                setQuery('');
                setOpen(false);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
        }
    };

    return (
        <div ref={containerRef} className={`relative w-full ${classList ?? ''}`} id={id} onKeyDown={handleKeyDown}>
            <button
                type="button"
                className={`bg-input-backgroundColor w-full text-left p-3 border border-2 border-input-borderColor text-layout-textcolor rounded-lg outline-none focus:border-primary transition-all duration-100 ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={() => !disabled && setOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={disabled}
            >
                {selectedItem ? (
                    <div className="flex flex-col">
                        <span className="truncate">{selectedItem.label}</span>
                        {selectedItem.subLabel && (
                            <span className="truncate text-xs text-layout-textcolor/70">{selectedItem.subLabel}</span>
                        )}
                    </div>
                ) : (
                    <>
                        <span className="text-layout-textcolor/70">
                            {placeholder ?? i18n.translate({ id: 'select.placeholder', sourceName: 'MCP' })}
                        </span>
                    </>
                )}
            </button>

            {open && !disabled && (
                <div
                    role="listbox"
                    tabIndex={-1}
                    className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-input-borderColor bg-input-backgroundColor shadow-lg"
                >
                    <div className="sticky top-0 bg-input-backgroundColor border-b border-input-borderColor p-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={'Filter...'}
                            className="w-full rounded-md border border-input-borderColor bg-input-backgroundColor px-2 py-1 text-sm outline-none focus:border-primary"
                            aria-label="Filter"
                        />
                    </div>

                    {filteredAndSortedItems.length === 0 && (
                        <div className="p-3 text-sm text-layout-textcolor/70">
                            {i18n.translate({ id: 'select.noOptions', sourceName: 'MCP' })}
                        </div>
                    )}
                    {filteredAndSortedItems.map((item, idx) => (
                        <div
                            key={String(item.value)}
                            role="option"
                            aria-selected={value === item.value}
                            className={`cursor-pointer px-3 py-2 hover:bg-input-backgroundHoverColor ${
                                idx === activeIndex ? 'bg-input-backgroundHoverColor' : ''
                            }`}
                            onMouseEnter={() => setActiveIndex(idx)}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onChange(item.value);
                                setQuery('');
                                setOpen(false);
                            }}
                        >
                            <div className="flex flex-col">
                                <span className="text-sm text-layout-textcolor truncate">{item.label}</span>
                                {item.subLabel && (
                                    <span className="text-xs text-layout-textcolor/70 truncate">{item.subLabel}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TwoLineSelect;
